import { useEffect, useState } from "react";
import { NotificationBell } from "../components/NotificationBell";
import { useNavigate } from "react-router-dom";
import api from "../api/client";
import { LineChart, Line, Area, AreaChart, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { isProjectAdmin } from "../utils/permissions";
import UserMenu from "../components/UserMenu";

export default function Dashboard() {
  const [reports, setReports] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const name = localStorage.getItem("name") || "User";
  const initials = name.split(" ").map((n: string) => n[0]).join("").toUpperCase();
  const projectId = localStorage.getItem("active_project_id") || "";

  useEffect(() => {
    api.get("/api/reports?project_id=" + projectId).then((r) => {
      setReports(r.data);
      setLoading(false);
    }).catch(() => navigate("/login"));
  }, [navigate]);

  const latest = reports[0];
  const metrics = latest?.output?.executive_summary?.metrics;
  const lights = latest?.output?.executive_summary?.traffic_lights;

  const trendData = reports.slice(0, 6).reverse().map((r: any) => ({
    quarter: r.quarter,
    cpi: r.output?.executive_summary?.metrics?.cpi,
    vac: r.output?.executive_summary?.metrics?.vac
      ? Math.abs(r.output.executive_summary.metrics.vac / 1000000)
      : null,
  }));

  const cpi = metrics?.cpi;
  const cpiPct = cpi ? Math.min(Math.max(((cpi - 0) / 1.2) * 100, 0), 100) : 50;
  const cpiColor = (v: number) => v < 0.9 ? "text-red-500" : v < 0.95 ? "text-amber-500" : "text-green-500";

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <p className="text-gray-500 text-sm">Loading...</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Nav */}
      <nav className="bg-white border-b border-gray-200 px-6 py-2.5 flex items-center justify-between relative z-50">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 bg-blue-600 rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-xs">A</span>
          </div>
          <div>
            <div className="text-xs text-gray-400">M170 BP884</div>
            <div className="text-sm font-semibold text-gray-900 leading-tight">EAC Reporting</div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => navigate("/dashboard/" + projectId)}
            className="px-4 py-1.5 bg-blue-600 text-white rounded-full text-sm font-medium">Dashboard</button>
          <button onClick={() => navigate("/reports")}
            className="px-4 py-1.5 text-gray-600 text-sm hover:text-gray-900">History</button>
          {isProjectAdmin(projectId) && (
            <button onClick={() => navigate(`/projects/${projectId}/members`)}
            className="px-4 py-1.5 text-gray-600 text-sm hover:text-gray-900">User Management</button>
          )}
          <button onClick={() => navigate("/compare")}
            className="px-4 py-1.5 text-gray-600 text-sm hover:text-gray-900">Compare</button>
          <button onClick={() => navigate("/users")}
            className="px-4 py-1.5 text-gray-600 text-sm hover:text-gray-900">Users</button>
          <button onClick={() => navigate("/settings")}
            className="px-4 py-1.5 text-gray-600 text-sm hover:text-gray-900">Settings</button>
        </div>
        <div className="flex items-center gap-3">
          <button className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-700">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </button>
          <NotificationBell />
          <UserMenu />
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">DASHBOARD</h1>
              <span className="px-2 py-0.5 bg-green-50 text-green-600 text-xs font-semibold rounded border border-green-200">ACTIVE</span>
            </div>
            <p className="text-xs text-gray-400 mt-0.5">
              {latest?.quarter} · LAST GENERATED {latest ? new Date(latest.created_at).toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" }).toUpperCase() : ""} · {name.toUpperCase()}
            </p>
          </div>
          <div className="flex gap-2">
            <button className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Export
            </button>
            <button onClick={() => navigate("/compare")}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              Compare
            </button>
            <button onClick={() => navigate("/upload/" + projectId)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
              + New Report
            </button>
          </div>
        </div>

        {/* Top 4 cards */}
        <div className="grid grid-cols-4 gap-4 mb-5">

          {/* Latest Report */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="w-8 h-8 bg-blue-50 border border-blue-100 rounded-lg flex items-center justify-center mb-3">
              <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Latest Report</p>
            <p className="text-2xl font-bold text-gray-900">{latest?.quarter || "None"}</p>
            <p className="text-xs text-gray-500 mt-1">{latest?.version_label} · by {name}</p>
            {latest && (
              <div className="flex items-center gap-1.5 mt-2">
                <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                <span className="text-xs text-blue-500">
                  {latest.status === "published" ? "Published" : "Draft"} · {new Date(latest.created_at).toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" })}
                </span>
              </div>
            )}
            {latest && (
              <button onClick={() => navigate(`/reports/${latest.id}`)}
                className="mt-3 text-xs text-blue-600 hover:underline flex items-center gap-1">
                View Report →
              </button>
            )}
          </div>

          {/* Report History */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="w-8 h-8 bg-blue-50 border border-blue-100 rounded-lg flex items-center justify-center mb-3">
              <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
              </svg>
            </div>
            <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Report History</p>
            <p className="text-2xl font-bold text-gray-900">{reports.length} Versions</p>
            <p className="text-xs text-gray-500 mt-1">
              Last: {latest ? new Date(latest.created_at).toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" }) : "—"}
            </p>
            {reports.length > 1 && (
              <div className="flex items-center gap-1.5 mt-2">
                <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                <span className="text-xs text-blue-500">Spanning {Math.min(reports.length, 4)} quarters</span>
              </div>
            )}
            <button onClick={() => navigate("/reports")}
              className="mt-3 text-xs text-blue-600 hover:underline flex items-center gap-1">
              View all versions →
            </button>
          </div>

          {/* Quick Stats */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs text-gray-400 uppercase tracking-wide">Quick Stats</p>
              {lights?.cost === "red" && (
                <span className="px-2 py-0.5 bg-red-50 text-red-500 text-xs rounded-full font-medium border border-red-100">● At Risk</span>
              )}
            </div>
            {metrics ? (
              <>
                <div className="space-y-2 mb-3">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-500">CPI</span>
                    <span className={`font-medium ${cpiColor(metrics.cpi)}`}>{metrics.cpi?.toFixed(3)} ●</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-500">SPI</span>
                    <span className="font-medium text-amber-500">{metrics.spi?.toFixed(3)} ●</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-500">VAC</span>
                    <span className="font-medium text-red-500">-${(Math.abs(metrics.vac) / 1000000).toFixed(1)}M</span>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-1">CPI Health Scale</p>
                  <div className="relative h-2 rounded-full overflow-hidden"
                    style={{ background: "linear-gradient(to right, #ef4444 0%, #ef4444 33%, #f59e0b 33%, #f59e0b 55%, #22c55e 55%, #22c55e 100%)" }}>
                    <div className="absolute w-2.5 h-2.5 bg-white border-2 border-gray-700 rounded-full shadow"
                      style={{ left: `${cpiPct}%`, top: "50%", transform: "translate(-50%, -50%)" }}></div>
                  </div>
                  <div className="flex justify-between text-xs text-gray-400 mt-1">
                    <span>0.0 Critical</span><span>0.90 Warn</span><span>0.95+ Ok</span>
                  </div>
                </div>
              </>
            ) : <p className="text-sm text-gray-400">No data yet</p>}
          </div>

          {/* Generate CTA */}
          <div className="bg-blue-600 rounded-xl p-4 flex flex-col justify-between relative overflow-hidden">
            <div className="absolute top-3 right-3 w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
              <span className="text-white text-xl font-light">+</span>
            </div>
            <div>
              <p className="text-xs text-blue-200 uppercase tracking-wide mb-1">New Report</p>
              <p className="text-xl font-bold text-white leading-tight uppercase">Generate Q2 2026</p>
              <p className="text-xs text-blue-200 mt-1">Upload files to get started</p>
            </div>
            <button onClick={() => navigate("/upload/" + projectId)}
              className="mt-3 border-2 border-dashed border-blue-400 border-opacity-60 rounded-lg py-2 text-sm font-medium text-white hover:bg-blue-500 flex items-center justify-center gap-2 transition-colors">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              Upload Files
            </button>
          </div>
        </div>

        {/* Bottom — Program Summary + Trends */}
        <div className="grid grid-cols-2 gap-4">

          {/* Program Summary */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <p className="text-sm font-semibold text-gray-800 mb-4">Program Summary — {latest?.quarter || "—"}</p>
            {metrics ? (
              <>
                <div className="grid grid-cols-3 gap-3 mb-5">
                  <div className="border border-gray-100 rounded-lg p-3">
                    <div className="flex items-center gap-1 mb-1">
                      <p className="text-xs text-gray-400 uppercase">CPI</p>
                      <span className="text-red-500 text-xs">●</span>
                    </div>
                    <p className={`text-2xl font-bold ${cpiColor(metrics.cpi)}`}>{metrics.cpi?.toFixed(3)}</p>
                    <p className="text-xs text-gray-400 mt-0.5">Cost Performance</p>
                    <p className="text-xs text-red-400 mt-1">-0.033 ↓</p>
                  </div>
                  <div className="border border-gray-100 rounded-lg p-3">
                    <div className="flex items-center gap-1 mb-1">
                      <p className="text-xs text-gray-400 uppercase">SPI</p>
                      <span className="text-amber-500 text-xs">●</span>
                    </div>
                    <p className="text-2xl font-bold text-amber-500">{metrics.spi?.toFixed(3)}</p>
                    <p className="text-xs text-gray-400 mt-0.5">Schedule Performance</p>
                  </div>
                  <div className="border border-gray-100 rounded-lg p-3">
                    <div className="flex items-center gap-1 mb-1">
                      <p className="text-xs text-gray-400 uppercase">VAC</p>
                      <span className="text-red-500 text-xs">●</span>
                    </div>
                    <p className="text-2xl font-bold text-red-500">-${(Math.abs(metrics.vac) / 1000000).toFixed(1)}M</p>
                    <p className="text-xs text-gray-400 mt-0.5">Variance at Completion</p>
                    <p className="text-xs text-red-400 mt-1">+${(Math.abs(metrics.vac) / 1000000).toFixed(1)}M ↑</p>
                  </div>
                  <div className="border border-gray-100 rounded-lg p-3">
                    <p className="text-xs text-gray-400 uppercase mb-1">EAC</p>
                    <p className="text-2xl font-bold text-gray-900">${(metrics.eac / 1000000).toFixed(1)}M</p>
                    <p className="text-xs text-gray-400 mt-0.5">Estimate at Completion</p>
                  </div>
                  <div className="border border-gray-100 rounded-lg p-3">
                    <p className="text-xs text-gray-400 uppercase mb-1">BAC</p>
                    <p className="text-2xl font-bold text-gray-900">${(metrics.bac / 1000000).toFixed(1)}M</p>
                    <p className="text-xs text-gray-400 mt-0.5">Budget at Completion</p>
                  </div>
                  <div className="border border-gray-100 rounded-lg p-3">
                    <p className="text-xs text-gray-400 uppercase mb-1">% Complete</p>
                    <p className="text-2xl font-bold text-green-500">
                      {metrics.percent_complete ? `${Math.round(metrics.percent_complete * 100)}%` : "81%"}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">Earned to Date</p>
                    <span className="inline-block mt-1 px-2 py-0.5 bg-green-50 text-green-600 text-xs rounded-full border border-green-100">On Track</span>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide mb-3">Workstream Progress</p>
                  <div className="space-y-3">
                    <div>
                      <div className="flex justify-between text-xs text-gray-600 mb-1">
                        <span>Workstream 1 — Avionics Core</span>
                        <span>100%</span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-green-500 rounded-full" style={{ width: "100%" }}></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-xs text-gray-600 mb-1">
                        <span>Workstream 2 — Integration & Test</span>
                        <span>60%</span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-amber-400 rounded-full" style={{ width: "60%" }}></div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <p className="text-sm text-gray-400">No report data yet. Generate a report to see metrics.</p>
            )}
          </div>

          {/* Trends */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <p className="text-sm font-semibold text-gray-900 mb-4">Trends (Last 4 Quarters)</p>
            {trendData.length > 0 ? (
              <>
                <div className="mb-5">
                  <div className="flex justify-between items-center mb-1">
                    <p className="text-xs text-gray-500">CPI Trend</p>
                    <span className="text-xs text-red-500">↘ Decline</span>
                  </div>
                  <div className="flex gap-3 text-xs text-gray-400 mb-1">
                    {trendData.slice(-4).map((d: any, i: number) => <span key={i}>{d.cpi ? d.cpi.toFixed(2) : "—"}</span>)}
                  </div>
                  <ResponsiveContainer width="100%" height={70}>
                    <AreaChart data={trendData}>
                      <defs>
                        <linearGradient id="cpiGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#ef4444" stopOpacity={0.15}/>
                          <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <YAxis domain={['auto', 'auto']} hide />
                      <Area type="monotone" dataKey="cpi" stroke="#ef4444" strokeWidth={2} fill="url(#cpiGrad)" dot={{ r: 3, fill: "#ef4444" }} />
                      <Tooltip formatter={(v: any) => v?.toFixed(3)} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <p className="text-xs text-gray-500">VAC Growth</p>
                    <span className="text-xs text-green-600">↗ Growing</span>
                  </div>
                  <div className="flex gap-3 text-xs text-gray-400 mb-1">
                    {trendData.slice(-4).map((d: any, i: number) => <span key={i}>{d.vac ? `-$${d.vac.toFixed(1)}M` : "—"}</span>)}
                  </div>
                  <ResponsiveContainer width="100%" height={70}>
                    <AreaChart data={trendData}>
                      <defs>
                        <linearGradient id="vacGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#22c55e" stopOpacity={0.15}/>
                          <stop offset="95%" stopColor="#22c55e" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <YAxis domain={['auto', 'auto']} hide />
                      <Area type="monotone" dataKey="vac" stroke="#22c55e" strokeWidth={2} fill="url(#vacGrad)" dot={{ r: 3, fill: "#22c55e" }} />
                      <Tooltip formatter={(v: any) => `$${v?.toFixed(1)}M`} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </>
            ) : (
              <p className="text-sm text-gray-400">Run multiple reports to see trends.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
